import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import {
  Users, Loader2, ArrowLeft, Database, BarChart3, Eye, Shield,
  DollarSign, Brain, Swords, Share2, AlertTriangle, Download, Package, Radar, Workflow, Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GenerateContentBundlePanel from "@/components/admin/GenerateContentBundlePanel";
import GenerateIntelligenceBatchPanel from "@/components/admin/GenerateIntelligenceBatchPanel";
import IntelligenceBatchActivity from "@/components/admin/IntelligenceBatchActivity";
import GeneratedAssetsQueue from "@/components/admin/GeneratedAssetsQueue";
import ActiveIntelligenceBiases from "@/components/admin/ActiveIntelligenceBiases";
import AutonomousStrategyQueue from "@/components/admin/AutonomousStrategyQueue";
import AssetPerformanceTelemetry from "@/components/admin/AssetPerformanceTelemetry";
import BehavioralCampaignBuilder from "@/components/admin/BehavioralCampaignBuilder";
import ActiveBehavioralCampaigns from "@/components/admin/ActiveBehavioralCampaigns";
import NarrativeEscalationMapping from "@/components/admin/NarrativeEscalationMapping";
import CampaignInjectionQueue from "@/components/admin/CampaignInjectionQueue";
import LiveBehavioralOperations from "@/components/admin/LiveBehavioralOperations";
import DeploymentCommandQueue from "@/components/admin/DeploymentCommandQueue";
import { SearchConsoleStatus } from "@/components/admin/SearchConsoleStatus";

interface PortalUser {
  user_id: string;
  email: string;
  has_purchased_starter: boolean;
  has_purchased_pro: boolean;
  shield_credits: number;
  created_at: string;
  research_consent: boolean;
}

interface FlaggedPhrase {
  id: string;
  created_at: string;
  raw_text: string;
  manipulation_score: number;
  manipulation_primary_type: string | null;
  content_status: string;
}

interface AdminData {
  totalUsers: number;
  paidUsers: number;
  starterOnly: number;
  proUsers: number;
  totalCurrentCredits: number;
  creditsBurned: number;
  totalNeutralizations: number;
  top10Patterns: Array<{ name: string; count: number }>;
  avgManipulationScore: string;
  flaggedPhrases: FlaggedPhrase[];
  totalRehearsals: number;
  completedRehearsals: number;
  completionRate: string;
  readinessDistribution: { low: number; medium: number; high: number };
  archetypeCounts: Record<string, number>;
  wasCopiedCount: number;
  viralScriptCount: number;
  socialRequestsCount: number;
  researchOptIn: number;
  researchOptOut: number;
  allUsersForTier: PortalUser[];
  downloadsByNiche: Record<string, number>;
  signupsByNiche: Record<string, number>;
  creatorsWhoConverted: number;
  recentCreatorRequests: Array<{ id: string; created_at: string; email: string; niche: string; pack_downloaded: boolean; download_count: number }>;
}

const formatPatternName = (name: string) =>
  name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const AdminPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState<AdminData | null>(null);
  const [viewDataUser, setViewDataUser] = useState<string | null>(null);
  const [userData, setUserData] = useState<any[]>([]);
  const [loadingUserData, setLoadingUserData] = useState(false);

  const fetchAdminData = async () => {
    if (!user) return null;
    try {
      const { data: resp, error } = await supabase.functions.invoke("admin-stats");
      if (error || !resp || resp.error) return null;
      return resp as AdminData;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const load = async () => {
      if (authLoading) return;
      if (!user) { navigate("/", { replace: true }); return; }
      const resp = await fetchAdminData();
      if (!resp) { navigate("/", { replace: true }); return; }
      setData(resp);
      setChecking(false);
    };
    load();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || checking) return;
    const channel = supabase
      .channel("admin-profiles-realtime")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles" }, async (payload) => {
        const changed = payload.new as any;
        setData((prev) => {
          if (!prev) return prev;
          const updated = prev.allUsersForTier.map((u) =>
            u.user_id === changed.user_id ? { ...u, has_purchased_starter: changed.has_purchased_starter, has_purchased_pro: changed.has_purchased_pro, shield_credits: changed.shield_credits, research_consent: changed.research_consent } : u
          );
          return { ...prev, allUsersForTier: updated };
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, checking]);

  if (authLoading || checking) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
        </div>
      </CommandLayout>
    );
  }

  if (!data) return null;

  const handleTierChange = async (userId: string, tier: string) => {
    try {
      const { error } = await supabase.functions.invoke("admin-stats", { body: { action: "update_tier", user_id: userId, tier } });
      if (error) throw error;
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, allUsersForTier: prev.allUsersForTier.map((u) => u.user_id === userId ? { ...u, has_purchased_pro: tier === "pro", has_purchased_starter: tier === "starter" || tier === "pro" } : u) };
      });
      toast({ title: "Tier updated", description: `Set to ${tier.toUpperCase()}.` });
    } catch {
      toast({ title: "Error", description: "Failed to update tier.", variant: "destructive" });
    }
  };

  const handleResearchToggle = async (userId: string, newVal: boolean) => {
    try {
      const { error } = await supabase.functions.invoke("admin-stats", { body: { action: "toggle_research", user_id: userId, research_consent: newVal } });
      if (error) throw error;
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, researchOptIn: prev.researchOptIn + (newVal ? 1 : -1), researchOptOut: prev.researchOptOut + (newVal ? -1 : 1), allUsersForTier: prev.allUsersForTier.map((u) => u.user_id === userId ? { ...u, research_consent: newVal } : u) };
      });
      toast({ title: "Updated", description: `Research consent ${newVal ? "enabled" : "disabled"}.` });
    } catch {
      toast({ title: "Error", description: "Failed to toggle.", variant: "destructive" });
    }
  };

  const handleViewData = async (userId: string) => {
    setViewDataUser(userId);
    setLoadingUserData(true);
    try {
      const { data: resp, error } = await supabase.functions.invoke("admin-stats", { body: { action: "view_user_data", user_id: userId } });
      if (error) throw error;
      setUserData(resp?.entries || []);
    } catch {
      toast({ title: "Error", description: "Failed to load user data.", variant: "destructive" });
    } finally {
      setLoadingUserData(false);
    }
  };

  const getUserTier = (u: PortalUser) => {
    if (u.has_purchased_pro) return "pro";
    if (u.has_purchased_starter) return "starter";
    return "new";
  };

  const patternChartData = data.top10Patterns.map((p) => ({
    name: formatPatternName(p.name),
    count: p.count,
  }));

  const readinessChartData = [
    { name: "Low (0-40)", value: data.readinessDistribution.low, fill: "hsl(0 85% 55%)" },
    { name: "Medium (41-70)", value: data.readinessDistribution.medium, fill: "hsl(45 100% 55%)" },
    { name: "High (71-100)", value: data.readinessDistribution.high, fill: "hsl(160 100% 45%)" },
  ];

  const archetypeData = Object.entries(data.archetypeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name: formatPatternName(name), count }));

  return (
    <CommandLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-foreground">Admin </span>
              <span className="text-gradient-cyan">Dashboard</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Full platform intelligence · Real-time data</p>
          </div>
          <Button variant="outline" size="sm" asChild className="border-glass-border text-muted-foreground hover:text-foreground">
            <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-2" />Back</Link>
          </Button>
        </div>

        <div className="mb-4">
          <SearchConsoleStatus />
        </div>

        <Tabs defaultValue="revenue" className="w-full">
          <TabsList className="bg-charcoal-medium/60 border border-glass-border mb-4">
            <TabsTrigger value="revenue" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <DollarSign className="w-4 h-4 mr-1.5" />Revenue
            </TabsTrigger>
            <TabsTrigger value="forensic" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Brain className="w-4 h-4 mr-1.5" />Forensic
            </TabsTrigger>
            <TabsTrigger value="rehearsal" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Swords className="w-4 h-4 mr-1.5" />Rehearsal
            </TabsTrigger>
            <TabsTrigger value="viral" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Share2 className="w-4 h-4 mr-1.5" />Viral
            </TabsTrigger>
            <TabsTrigger value="strategy" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Workflow className="w-4 h-4 mr-1.5" />Strategy
            </TabsTrigger>
            <Link
              to="/admin/intelligence"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all hover:bg-cyan/10 hover:text-cyan text-muted-foreground"
            >
              <Radar className="w-4 h-4 mr-1.5" />Intelligence
            </Link>
            <Link
              to="/admin/analytics"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all hover:bg-cyan/10 hover:text-cyan text-muted-foreground"
            >
              <Activity className="w-4 h-4 mr-1.5" />Attribution
            </Link>
            <TabsTrigger value="users" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Users className="w-4 h-4 mr-1.5" />Users
            </TabsTrigger>
            <TabsTrigger value="creators" className="data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">
              <Package className="w-4 h-4 mr-1.5" />Creators
            </TabsTrigger>
          </TabsList>

          {/* ===== SECTION 1: Revenue & Credits ===== */}
          <TabsContent value="revenue" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={data.totalUsers} icon={<Users className="w-4 h-4 text-cyan" />} />
              <StatCard title="Paid Users" value={data.paidUsers} icon={<DollarSign className="w-4 h-4 text-cyan" />} sub={`${data.totalUsers > 0 ? ((data.paidUsers / data.totalUsers) * 100).toFixed(1) : 0}% conversion`} />
              <StatCard title="Starter Tier" value={data.starterOnly} icon={<Shield className="w-4 h-4 text-cyan" />} />
              <StatCard title="Pro Tier" value={data.proUsers} icon={<Shield className="w-4 h-4 text-amber-400" />} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard title="Credits Burned (est.)" value={data.creditsBurned} icon={<BarChart3 className="w-4 h-4 text-cyan" />} sub={`${data.totalCurrentCredits} credits remaining across all users`} />
              <StatCard title="Total Analyses" value={data.totalNeutralizations} icon={<Brain className="w-4 h-4 text-cyan" />} sub="All-time neutralizations processed" />
            </div>
          </TabsContent>

          {/* ===== SECTION 2: Forensic Intelligence ===== */}
          <TabsContent value="forensic" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard title="Avg Manipulation Score" value={data.avgManipulationScore} icon={<Brain className="w-4 h-4 text-cyan" />} />
              <StatCard title="Total Analyses" value={data.totalNeutralizations} icon={<Database className="w-4 h-4 text-cyan" />} />
              <StatCard title="Flagged (≥9.5)" value={data.flaggedPhrases.length} icon={<AlertTriangle className="w-4 h-4 text-red-400" />} />
            </div>

            {/* Top 10 Patterns */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan" />Top 10 Manipulation Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={patternChartData} layout="vertical" margin={{ left: 120 }}>
                    <XAxis type="number" tick={{ fill: "hsl(0 0% 60%)", fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "hsl(0 0% 75%)", fontSize: 11 }} width={110} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(0 0% 12%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 8, color: "hsl(0 0% 90%)" }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="hsl(184 100% 50%)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Flagged Phrases */}
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />Flagged Phrases (Score ≥ 9.5) — Creator Pack
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-glass-border hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Date</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Score</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Pattern</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Raw Text</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-[120px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.flaggedPhrases.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No flagged phrases found.</TableCell></TableRow>
                      ) : data.flaggedPhrases.map((f) => (
                        <TableRow key={f.id} className="border-glass-border">
                          <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="font-mono font-bold text-sm text-red-400">{Number(f.manipulation_score).toFixed(1)}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs border-cyan/30 text-cyan">{f.manipulation_primary_type ? formatPatternName(f.manipulation_primary_type) : "—"}</Badge></TableCell>
                          <TableCell className="max-w-[300px]"><p className="text-xs text-muted-foreground line-clamp-2">{f.raw_text}</p></TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-400/10" onClick={() => { navigator.clipboard.writeText(f.raw_text); toast({ title: "Copied", description: "Phrase copied for Creator Pack." }); }}>
                              <Download className="w-3 h-3 mr-1" /><span className="text-xs">Export</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== SECTION 3: Rehearse Mode Analytics ===== */}
          <TabsContent value="rehearsal" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard title="Sessions Started" value={data.totalRehearsals} icon={<Swords className="w-4 h-4 text-cyan" />} />
              <StatCard title="Completed" value={data.completedRehearsals} icon={<Swords className="w-4 h-4 text-green-400" />} />
              <StatCard title="Completion Rate" value={`${data.completionRate}%`} icon={<BarChart3 className="w-4 h-4 text-cyan" />} />
              <StatCard title="Dropped" value={data.totalRehearsals - data.completedRehearsals} icon={<AlertTriangle className="w-4 h-4 text-red-400" />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Readiness Distribution */}
              <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-cyan" />Readiness Score Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={readinessChartData}>
                      <XAxis dataKey="name" tick={{ fill: "hsl(0 0% 70%)", fontSize: 11 }} />
                      <YAxis tick={{ fill: "hsl(0 0% 60%)", fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(0 0% 12%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 8, color: "hsl(0 0% 90%)" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {readinessChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Archetype Frequency */}
              <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-cyan" />Tactics Deployed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {archetypeData.length === 0 ? (
                    <p className="text-muted-foreground text-sm text-center py-8">No tactic data yet.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={archetypeData} layout="vertical" margin={{ left: 100 }}>
                        <XAxis type="number" tick={{ fill: "hsl(0 0% 60%)", fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: "hsl(0 0% 75%)", fontSize: 11 }} width={90} />
                        <Tooltip contentStyle={{ backgroundColor: "hsl(0 0% 12%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 8, color: "hsl(0 0% 90%)" }} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="hsl(45 100% 55%)" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== SECTION 4: Viral Content Tracking ===== */}
          <TabsContent value="viral" className="space-y-4">
            <DeploymentCommandQueue />
            <AutonomousStrategyQueue />
            <CampaignInjectionQueue />
            <ActiveIntelligenceBiases />
            <GenerateIntelligenceBatchPanel />
            <IntelligenceBatchActivity />
            <GenerateContentBundlePanel />
            <GeneratedAssetsQueue />
            <AssetPerformanceTelemetry />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard title="Responses Copied" value={data.wasCopiedCount} icon={<Share2 className="w-4 h-4 text-cyan" />} sub="was_copied flag count" />
              <StatCard title="Scripts Generated" value={data.viralScriptCount} icon={<Download className="w-4 h-4 text-amber-400" />} sub="Script factory outputs" />
              <StatCard title="Social Requests" value={data.socialRequestsCount} icon={<Share2 className="w-4 h-4 text-cyan" />} sub="Total share card requests" />
            </div>
          </TabsContent>

          {/* ===== SECTION: Strategy — Behavioral Campaign OS ===== */}
          <TabsContent value="strategy" className="space-y-4">
            <LiveBehavioralOperations />
            <BehavioralCampaignBuilder />
            <ActiveBehavioralCampaigns />
            <NarrativeEscalationMapping />
          </TabsContent>

          {/* ===== USER MANAGEMENT ===== */}
          <TabsContent value="users" className="space-y-4">
            <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan" />User Management
                  <span className="text-xs font-normal text-muted-foreground ml-2">{data.allUsersForTier.length} users</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-glass-border hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Email</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tier</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Research</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Credits</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Joined</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.allUsersForTier.map((u) => (
                        <TableRow key={u.user_id} className="border-glass-border">
                          <TableCell className="text-xs text-foreground font-mono">{u.email}</TableCell>
                          <TableCell>
                            <Select value={getUserTier(u)} onValueChange={(val) => handleTierChange(u.user_id, val)}>
                              <SelectTrigger className="h-7 w-[100px] text-xs bg-charcoal-deep border-glass-border">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">NEW</SelectItem>
                                <SelectItem value="starter">STARTER</SelectItem>
                                <SelectItem value="pro">PRO</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Switch checked={u.research_consent} onCheckedChange={(val) => handleResearchToggle(u.user_id, val)} className="data-[state=checked]:bg-cyan" />
                          </TableCell>
                          <TableCell className="text-xs font-mono text-foreground">{u.shield_credits}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {u.research_consent && (
                              <Button variant="ghost" size="sm" onClick={() => handleViewData(u.user_id)} className="h-7 px-2 text-cyan hover:text-cyan hover:bg-cyan/10">
                                <Eye className="w-3 h-3 mr-1" /><span className="text-xs">View</span>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== SECTION 6: Creator Pack Analytics ===== */}
          <TabsContent value="creators" className="space-y-4">
            {(() => {
              const allNiches = [...new Set([...Object.keys(data.downloadsByNiche || {}), ...Object.keys(data.signupsByNiche || {})])];
              const downloadsChartData = allNiches.map((n) => ({ name: formatPatternName(n), count: (data.downloadsByNiche || {})[n] || 0 })).sort((a, b) => b.count - a.count);
              const signupsChartData = allNiches.map((n) => ({ name: formatPatternName(n), count: (data.signupsByNiche || {})[n] || 0 })).sort((a, b) => b.count - a.count);

              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatCard title="Total Creator Signups" value={Object.values(data.signupsByNiche || {}).reduce((a: number, b: number) => a + b, 0)} icon={<Package className="w-4 h-4 text-cyan" />} />
                    <StatCard title="Total Downloads" value={Object.values(data.downloadsByNiche || {}).reduce((a: number, b: number) => a + b, 0)} icon={<Download className="w-4 h-4 text-amber-400" />} />
                    <StatCard title="Creators Who Became Users" value={data.creatorsWhoConverted || 0} icon={<Users className="w-4 h-4 text-green-400" />} sub="Creator emails matching app signups" />
                  </div>

                  {/* Downloads by Niche */}
                  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-cyan" />Downloads by Niche
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {downloadsChartData.length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-8">No downloads yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={downloadsChartData} layout="vertical" margin={{ left: 140 }}>
                            <XAxis type="number" tick={{ fill: "hsl(0 0% 60%)", fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" tick={{ fill: "hsl(0 0% 75%)", fontSize: 11 }} width={130} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(0 0% 12%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 8, color: "hsl(0 0% 90%)" }} />
                            <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="hsl(184 100% 50%)" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>

                  {/* Signups by Niche */}
                  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-amber-400" />Notify Me Signups by Niche
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {signupsChartData.length === 0 ? (
                        <p className="text-muted-foreground text-sm text-center py-8">No signups yet.</p>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={signupsChartData} layout="vertical" margin={{ left: 140 }}>
                            <XAxis type="number" tick={{ fill: "hsl(0 0% 60%)", fontSize: 11 }} />
                            <YAxis type="category" dataKey="name" tick={{ fill: "hsl(0 0% 75%)", fontSize: 11 }} width={130} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(0 0% 12%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 8, color: "hsl(0 0% 90%)" }} />
                            <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="hsl(45 100% 55%)" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>

                  {/* Conversion Rate by Niche */}
                  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-green-400" />Conversion Rate by Niche
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {allNiches.map((niche) => {
                          const signups = (data.signupsByNiche || {})[niche] || 0;
                          const downloads = (data.downloadsByNiche || {})[niche] || 0;
                          const rate = signups > 0 ? ((downloads / signups) * 100).toFixed(1) : "0.0";
                          return (
                            <div key={niche} className="rounded-lg border border-glass-border bg-charcoal-deep/60 p-3 text-center">
                              <p className="text-xs text-muted-foreground mb-1 capitalize">{formatPatternName(niche)}</p>
                              <p className="text-2xl font-bold font-mono text-foreground">{rate}%</p>
                              <p className="text-[10px] text-muted-foreground">{downloads}/{signups} converted</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Requests */}
                  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Database className="w-5 h-5 text-cyan" />Recent Creator Requests
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-glass-border hover:bg-transparent">
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Date</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Email</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Niche</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Downloaded</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Count</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(data.recentCreatorRequests || []).length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No creator requests yet.</TableCell></TableRow>
                          ) : (data.recentCreatorRequests || []).map((r) => (
                            <TableRow key={r.id} className="border-glass-border">
                              <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                              <TableCell className="text-xs font-mono text-foreground">{r.email.length > 20 ? r.email.slice(0, 8) + "…" + r.email.slice(r.email.indexOf("@")) : r.email}</TableCell>
                              <TableCell><Badge variant="outline" className="text-xs border-cyan/30 text-cyan capitalize">{formatPatternName(r.niche)}</Badge></TableCell>
                              <TableCell>{r.pack_downloaded ? <Badge className="bg-green-500/15 text-green-400 border-green-500/30 text-[10px]">YES</Badge> : <Badge className="bg-muted text-muted-foreground border-border text-[10px]">NO</Badge>}</TableCell>
                              <TableCell className="text-xs font-mono text-foreground">{r.download_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>

      {/* View User Data Dialog */}
      <Dialog open={!!viewDataUser} onOpenChange={(open) => { if (!open) setViewDataUser(null); }}>
        <DialogContent className="max-w-2xl bg-charcoal-medium border-glass-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Anonymized User Data</DialogTitle>
          </DialogHeader>
          {loadingUserData ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
          ) : userData.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">No anonymized data available.</p>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-glass-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Cat</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tox</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Text</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userData.map((entry: any) => (
                    <TableRow key={entry.id} className="border-glass-border">
                      <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{new Date(entry.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs border-cyan/30 text-cyan uppercase">{entry.category}</Badge></TableCell>
                      <TableCell className="font-mono font-bold text-sm text-foreground">{Number(entry.toxicity_score).toFixed(1)}</TableCell>
                      <TableCell className="max-w-[300px]"><p className="text-xs text-muted-foreground line-clamp-2">{entry.anonymized_text}</p></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </CommandLayout>
  );
};

const StatCard = ({ title, value, icon, sub }: { title: string; value: string | number; icon: React.ReactNode; sub?: string }) => (
  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <div className="p-2 rounded-lg bg-cyan/10">{icon}</div>
    </CardHeader>
    <CardContent>
      <p className="text-3xl font-bold font-mono text-foreground">{typeof value === "number" ? value.toLocaleString() : value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

export default AdminPortal;
